import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';

interface VerificationCongratsModalProps {
  open: boolean;
  onClose: () => void;
}

export const VerificationCongratsModal: React.FC<VerificationCongratsModalProps> = ({
  open,
  onClose,
}) => {
  const [hasShownConfetti, setHasShownConfetti] = useState(false);

  useEffect(() => {
    if (open && !hasShownConfetti) {
      // Trigger confetti animation
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

      const randomInRange = (min: number, max: number) => {
        return Math.random() * (max - min) + min;
      };

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          clearInterval(interval);
          return;
        }

        const particleCount = 50 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        });
      }, 250);

      setHasShownConfetti(true);

      return () => clearInterval(interval);
    }
  }, [open, hasShownConfetti]);

  // Reset confetti state when modal closes
  useEffect(() => {
    if (!open) {
      setHasShownConfetti(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md border-primary/30">
        <div className="space-y-6 py-6 text-center">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", duration: 0.8, bounce: 0.5 }}
            className="flex justify-center"
          >
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-white" />
              </div>
              
              {/* Sparkles around the icon */}
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ 
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                  style={{
                    top: '50%',
                    left: '50%',
                    transform: `rotate(${i * 45}deg) translateY(-50px)`,
                  }}
                >
                  <Sparkles className="w-4 h-4 text-primary" />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-2"
          >
            <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Welcome to the Community!
            </h2>
            <p className="text-muted-foreground">
              You're verified as a real human creator 🎉
            </p>
          </motion.div>

          {/* Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="space-y-3 text-sm"
          >
            <p className="text-foreground">
              You can now share your creativity with the world!
            </p>
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-2 text-left">
              <p className="font-medium text-foreground">What you can do now:</p>
              <ul className="text-xs text-muted-foreground space-y-1 ml-4 list-disc">
                <li>Create posts and share your work</li>
                <li>Upload images and videos</li>
                <li>Join community conversations</li>
                <li>Build your creative portfolio</li>
              </ul>
            </div>
          </motion.div>

          {/* Action Button */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 }}
          >
            <Button 
              onClick={onClose} 
              size="lg" 
              className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90"
            >
              Start Creating
            </Button>
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
